import { Swiper, SwiperSlide } from 'swiper/react'
import type { Swiper as SwiperType } from 'swiper'
import SwiperCore from 'swiper'
import { Virtual } from 'swiper/modules'
import 'swiper/css'
import 'swiper/css/navigation'
import 'swiper/css/pagination'

// Register Swiper modules
SwiperCore.use([Virtual])

import styles from './TilesCarousel.module.sass'
import type { TileListResult } from '../data/TileListFragment'
import { useRef, useState, useEffect } from 'react'
import { Icon } from './Icon'
import { ResponsiveImage } from './ResponsiveImage'
import Image from 'next/image'
import Link from 'next/link'
import { ContentRenderer } from './ContentRenderer'
import type { ContentResult } from '../data/ContentFragment'
import clsx from 'clsx'
import { fixCzechTypographyText } from '../language/czechLanguageFixer'
import type { PostTileResult } from '../data/PostTileFragment'

type TilesCarouselProps = {
	tileList: TileListResult
}

export const TilesCarousel = ({ tileList }: TilesCarouselProps) => {
	const swiperRef = useRef<SwiperType | null>(null)
	const [isBeginning, setIsBeginning] = useState(true)
	const [isEnd, setIsEnd] = useState(false)
	const [activeIndex, setActiveIndex] = useState(0)
	const [totalSlides, setTotalSlides] = useState(0)

	const slidePrev = () => {
		swiperRef.current?.slidePrev()
	}

	const slideNext = () => {
		swiperRef.current?.slideNext()
	}

	useEffect(() => {
		if (swiperRef.current) {
			setTotalSlides(swiperRef.current.slides.length)
		}
	}, [])

	return (
		<div className={styles.carouselContainer}>
			{tileList.title && <h2 className={styles.heading}>{fixCzechTypographyText(tileList.title)}</h2>}
			{tileList.areaType?.icon && (
				<div>
					{tileList.areaType?.localesByLocale?.link?.url ? (
						<Link className={styles.areaType} href={tileList.areaType?.localesByLocale?.link?.url}>
							<Image src={tileList?.areaType?.icon?.url || ''} width={36} height={36} alt="" className={styles.image} />
							<p>{tileList.areaType?.localesByLocale?.name}</p>
						</Link>
					) : (
						<div className={styles.areaType}>
							<Image src={tileList?.areaType?.icon?.url || ''} width={36} height={36} alt="" className={styles.image} />
							<p>{tileList.areaType?.localesByLocale?.name}</p>
						</div>
					)}
				</div>
			)}
			<div className={styles.swiperWrapper}>
				{!isBeginning && (
					<button
						type="button"
						className={`${styles.button} ${styles.view_previous}`}
						onClick={slidePrev}
						aria-label="previous"
					>
						<Icon name="caret" className={styles.buttonIcon} />
					</button>
				)}
				<Swiper
					onSwiper={(swiper) => {
						swiperRef.current = swiper
						setIsBeginning(swiper.isBeginning)
						setIsEnd(swiper.isEnd)
						setTotalSlides(swiper.slides.length)
					}}
					onSlideChange={(swiper) => {
						setIsBeginning(swiper.isBeginning)
						setActiveIndex(swiper.activeIndex)

						// Custom logic for end detection
						// Mark as end only when we're at the last possible slide position
						const currentSlidesPerView =
							typeof swiper.params.slidesPerView === 'number' ? swiper.params.slidesPerView : 1
						const isLastSlideVisible = swiper.activeIndex >= swiper.slides.length - currentSlidesPerView
						setIsEnd(isLastSlideVisible)
					}}
					onReachEnd={() => {
						setIsEnd(true)
					}}
					loop={false}
					spaceBetween={12}
					slidesPerView={1.5}
					cssMode={true}
					breakpoints={{
						640: {
							slidesPerView: 2,
						},
						1024: {
							slidesPerView: 3.5,
						},
					}}
					className={styles.swiper}
				>
					{tileList.items.map((tile, index) => {
						const isOrg = !!tile.organization
						const isArea = !!tile.area && !tile.organization && !tile.post
						const url =
							tile.post?.activeRevision?.post?.localesByLocale?.link?.url ||
							tile.organization?.localesByLocale?.link?.url ||
							tile.area?.localesByLocale?.link?.url ||
							'#'

						const image =
							tile.post?.activeRevision?.heroImage || tile.organization?.plainIcon || tile.area?.icon || tile.image

						const cardTitle =
							tile.post?.activeRevision?.localesByLocale?.title ||
							tile.organization?.localesByLocale?.name ||
							tile.area?.localesByLocale?.name ||
							tile.title ||
							''

						// Get description from TileItem directly
						const cardDescription = tile.description || ''

						return (
							<SwiperSlide key={url}>
								<div className={styles.card}>
									<a href={url} className={styles.cardImageContainer}>
										{image?.url && (
											<div
												className={styles.cardImage}
												style={
													isOrg && tile.organization?.color
														? { backgroundColor: tile.organization.color }
														: isArea && 'color' in (tile.area || {})
														? { backgroundColor: tile.area?.color }
														: {}
												}
											>
												<ResponsiveImage
													src={image.url}
													alt={cardTitle}
													fill={true}
													sizes="(min-width: 768px) 200px, 50vw"
													focalPointX={image.focalPointX ?? 0.5}
													focalPointY={image.focalPointY ?? 0.5}
													className={clsx(
														styles.cardImageInner,
														isOrg && styles.cardImageInnerSmallerOrg,
														isArea && styles.cardImageInnerSmallerArea,
													)}
												/>
												<div className={styles.overlay} />
											</div>
										)}
									</a>
									<div className={styles.cardContent}>
										<a href={url} style={{ textDecoration: 'none' }}>
											<h3 className={styles.cardTitle}>{fixCzechTypographyText(cardTitle)}</h3>
										</a>
										{tile.type === 'post' && tile.post?.activeRevision?.localesByLocale?.leadContent && (
											<div className={styles.cardMeta}>
												<ContentRenderer
													noPaddingLastBlock
													containerDisableGutters
													content={tile.post.activeRevision.localesByLocale.leadContent as ContentResult}
												/>
											</div>
										)}
									</div>
								</div>
							</SwiperSlide>
						)
					})}
				</Swiper>
				{!isEnd && (
					<button
						type="button"
						className={`${styles.button} ${styles.view_next}`}
						onClick={slideNext}
						aria-label="next"
					>
						<Icon name="caret" />
					</button>
				)}
			</div>
		</div>
	)
}

type Postcarousel = {
	posts: Array<PostTileResult>
}

export const PostCarousel = ({ posts }: Postcarousel) => {
	const swiperRef = useRef<SwiperType | null>(null)
	const [isBeginning, setIsBeginning] = useState(true)
	const [isEnd, setIsEnd] = useState(false)

	const slidePrev = () => {
		swiperRef.current?.slidePrev()
	}

	const slideNext = () => {
		swiperRef.current?.slideNext()
	}

	return (
		<div className={styles.carouselContainer}>
			<div className={styles.swiperWrapper}>
				{!isBeginning && (
					<button
						type="button"
						className={`${styles.button} ${styles.view_previous}`}
						onClick={slidePrev}
						aria-label="previous"
					>
						<Icon name="caret" className={styles.buttonIcon} />
					</button>
				)}
				<Swiper
					onSwiper={(swiper) => {
						swiperRef.current = swiper
						setIsBeginning(swiper.isBeginning)
						setIsEnd(swiper.isEnd)
					}}
					onSlideChange={(swiper) => {
						setIsBeginning(swiper.isBeginning)

						const currentSlidesPerView =
							typeof swiper.params.slidesPerView === 'number' ? swiper.params.slidesPerView : 1
						const isLastSlideVisible = swiper.activeIndex >= swiper.slides.length - currentSlidesPerView
						setIsEnd(isLastSlideVisible)
					}}
					onReachEnd={() => {
						setIsEnd(true)
					}}
					loop={false}
					spaceBetween={12}
					slidesPerView={1.5}
					cssMode={true}
					breakpoints={{
						640: {
							slidesPerView: 2,
						},
						1024: {
							slidesPerView: 3.5,
						},
					}}
					className={styles.swiper}
				>
					{posts.map((tile) => {
						const url = tile.activeRevision?.post?.localesByLocale?.link?.url

						const image = tile.activeRevision?.heroImage

						const cardTitle = tile?.activeRevision?.localesByLocale?.title

						return (
							<SwiperSlide key={url}>
								<div className={styles.card}>
									<a href={url} className={styles.cardImageContainer}>
										{image?.url && (
											<div className={styles.cardImage}>
												<ResponsiveImage
													src={image.url}
													alt={cardTitle || ''}
													fill={true}
													sizes="(min-width: 768px) 270px, 50vw"
													focalPointX={image.focalPointX ?? 0.5}
													focalPointY={image.focalPointY ?? 0.5}
													className={clsx(styles.cardImageInner)}
												/>
												<div className={styles.overlay} />
											</div>
										)}
									</a>
									<div className={styles.cardContent2}>
										<h3 className={styles.cardTitle}>{fixCzechTypographyText(cardTitle || '')}</h3>
									</div>
								</div>
							</SwiperSlide>
						)
					})}
				</Swiper>
				{!isEnd && (
					<button
						type="button"
						className={`${styles.button} ${styles.view_next}`}
						onClick={slideNext}
						aria-label="next"
					>
						<Icon name="caret" />
					</button>
				)}
			</div>
		</div>
	)
}
